import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} YunaDiscountBadgeRefs
 * @property {HTMLElement} badgeContent
 * @property {HTMLElement} productBadges
 * @property {HTMLElement} variantBadges
 */

/**
 * Badge row above the product title. Owns two containers:
 * - [ref="productBadges"] — static list from yuna_product.badges
 * - [ref="variantBadges"] — discount pill + yuna_pack.badges for the
 *   selected variant, rebuilt from window.yunaBadgeData on variant change
 *
 * Substitution rule (per Per-Variant Badge SPEC):
 *   If the selected variant has a discount OR any yuna_pack.badges →
 *     show the variant container, hide the product container.
 *   Else if the product has yuna_product.badges →
 *     show the product container, hide the variant container.
 *   Else →
 *     collapse the wrapper entirely.
 *
 * Hidden↔visible state is now toggled instantly via the
 * `.yuna-discount-badge--hidden` class (CSS controls visibility only — no
 * transition). Custom motion was removed from this file as part of the
 * site-wide motion-strip pass.
 *
 * @extends {Component<YunaDiscountBadgeRefs>}
 */
class YunaDiscountBadge extends Component {
  /** @type {Element | null} Where the product select listener is attached. */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    // Horizon 4.1.5 announces a variant change with Shopify's standard product
    // select event; Horizon 3's variant:update, which this badge used to wait
    // for, is no longer dispatched. The section is kept so the listener can be
    // removed after this element is detached, when closest() finds nothing.
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.handleProductSelect);
    this.#section = null;
  }

  /**
   * The selected variant is on the event immediately, so the badge swaps on
   * click; the event's promise covers selections that only resolve to a variant
   * once the section fetch returns.
   * @param {Event} event - Shopify's standard product select event
   */
  handleProductSelect = (event) => {
    const owner = event.target instanceof Element ? event.target.closest('[data-product-id]') : null;
    const productId = owner?.getAttribute('data-product-id');
    const { detail, promise } = /** @type {{ detail?: any, promise?: Promise<any> }} */ (/** @type {unknown} */ (event));

    if (detail?.variantId) {
      this.updateBadge(productId, detail.variantId);
      return;
    }
    promise
      ?.then((result) => this.updateBadge(result?.detail?.productId ?? productId, result?.detail?.resource?.id))
      .catch(() => {});
  };

  /**
   * @param {string | null | undefined} productId
   * @param {string | number | null | undefined} variantId
   */
  updateBadge = (productId, variantId) => {
    if (!variantId || !productId) return;

    // Combined listings can mount more than one badge block on a page;
    // only react to updates for our own product.
    if (String(productId) !== String(this.dataset.productId)) return;

    const variantData = window.yunaBadgeData?.[productId]?.[variantId];
    if (!variantData) return;

    const { productBadges, variantBadges } = this.refs;
    if (!productBadges || !variantBadges) return;

    const hasDiscount = Boolean(variantData.discount);
    const hasCustomBadges = Array.isArray(variantData.badges) && variantData.badges.length > 0;
    const hasProductBadges = this.dataset.hasProductBadges === 'true';

    let targetMode = 'empty';
    if (hasDiscount || hasCustomBadges) targetMode = 'variant';
    else if (hasProductBadges) targetMode = 'product';

    // Rebuild the variant container from data — cheap, and keeps the
    // markup authoritative relative to window.yunaBadgeData.
    this.#populateVariant(variantBadges, variantData);

    const applyContainerVisibility = () => {
      productBadges.hidden = targetMode !== 'product';
      variantBadges.hidden = targetMode !== 'variant';
    };

    const willBeHidden = targetMode === 'empty';
    const isCurrentlyHidden = this.classList.contains('yuna-discount-badge--hidden');

    if (willBeHidden === isCurrentlyHidden) {
      applyContainerVisibility();
      return;
    }

    if (willBeHidden) {
      this.classList.add('yuna-discount-badge--hidden');
      applyContainerVisibility();
      return;
    }

    applyContainerVisibility();
    this.classList.remove('yuna-discount-badge--hidden');
  };

  #populateVariant(container, data) {
    container.replaceChildren();
    if (data.discount) {
      container.append(this.#createPill(data.discount));
    }
    if (Array.isArray(data.badges)) {
      for (const b of data.badges) {
        container.append(this.#createPill(b.label, b.bg, b.color));
      }
    }
  }

  #createPill(label, bg, color) {
    const span = document.createElement('span');
    span.className = 'yuna-pill yuna-font--area-extended-bold';
    span.style.setProperty('--yuna-pill-bg', bg || '#CC4B2C');
    span.style.setProperty('--yuna-pill-color', color || '#FFFFFF');
    span.textContent = label;
    return span;
  }
}

if (!customElements.get('yuna-discount-badge')) {
  customElements.define('yuna-discount-badge', YunaDiscountBadge);
}
